import { For, Match, Show, Switch, createEffect, createMemo, createSignal } from 'solid-js'
import { Message, MessageRole, UserRating } from 'st-app/api'
import { AgentRationaleEvent, parseTrace, requireSuppportAssistAgentContext } from '../SupportAssistAgentContext'
import { toast } from 'common'
import styles from '../st-support-assist-agent.module.css'
import Button from '../../st-button/st-button'
import Markdown from '../../st-markdown/st-markdown'
import { showEmailDraftModal } from './EmailDraftModal'

export function ConversationComponent() {
    const context = requireSuppportAssistAgentContext();
    let messagesContainer: HTMLDivElement | undefined;
    const messages = createMemo(() => context.conversation.messages);

    createEffect(() => {
        const _ = messages(); // Track messages
        
        requestAnimationFrame(() => {
            // Scroll to the bottom of the messages container when messages change
            messagesContainer?.scrollTo({ top: messagesContainer.scrollHeight });
        })
    })

    return (
        <div class={styles['conversation']} data-id={context.conversation.conversationGU}>
            <Show when={context.conversation.messages.length} fallback={<NewConversationNote />}>
                <Show when={context.conversation.messages} fallback={<div>No conversation found.</div>}>
                    <div class={styles['messages-container']} ref={messagesContainer}>
                        <For each={[...context.conversation.messages]}>{(message) => (
                            <MessageComponent message={message} />
                        )}</For>
                    </div>
                </Show>
            </Show>

            <ChatInput />
        </div>
    );
}

function NewConversationNote() {
    const context = requireSuppportAssistAgentContext();
    const [analyzing, setAnalyzing] = createSignal(false);

    const handleStarterPrompt = async () => {
        setAnalyzing(true);
        try {
            await context.sendStarterPrompt();
        } finally {
            setAnalyzing(false);
        }
    };

    return (
        <div class={`${styles['new-conversation-note']}`}>
            <span style='font-size: 3em;'>Support Assist</span>
            <div style='font-size: 1.1em; margin-top: 8px;'>
                <span>Type a question or select a conversation to view details.</span>
            </div>
            <Show when={context.ticketGU()}>
                <button type='button' class={styles['starter-prompt']} onClick={handleStarterPrompt} disabled={analyzing()}>
                    <Show when={analyzing()} fallback={<>Analyze Ticket and Generate Response</>}>
                        <i class='fa fa-spinner fa-spin' style='margin-right: 6px' />
                        Analyzing ticket...
                    </Show>
                </button>
            </Show>
        </div>
    )
}

function MessageComponent(props: { message: Message }) {
    const context = requireSuppportAssistAgentContext();

    return (
        <div class={styles['message']} data-sender={props.message.role}>
            <div class={styles['message-content']}>
                <Switch>
                    <Match when={props.message.role === MessageRole.Assistant}>
                        <AssistantMessage message={props.message} />
                    </Match>
                    <Match when={props.message.role === MessageRole.User}>
                        <Markdown content={props.message.text} breaks />
                    </Match>
                </Switch>
            </div>

            <Show when={props.message.role === MessageRole.Assistant}>
                <div class={styles['message-actions']}>
                    <Button icon='fa-regular fa-copy' type='ghost' aria-label='Copy message' onClick={() => {
                        navigator.clipboard.writeText(props.message.text || '')
                            .then(() => toast.notify('success', 'Copied to clipboard.'))
                            .catch(() => toast.notify('error', 'Failed to copy to clipboard.'));
                    }} />

                    <Button
                        class={`${props.message.rating === UserRating.Positive ? styles['liked'] : ''}`}
                        icon='fa-regular fa-thumbs-up'
                        type='ghost'
                        aria-label='Like message'
                        onClick={() => context.likeMessage(props.message)}
                    />
                    <Button
                        class={`${props.message.rating === UserRating.Negative ? styles['disliked'] : ''}`}
                        icon='fa-regular fa-thumbs-down'
                        type='ghost'
                        aria-label='Dislike message'
                        onClick={() => context.dislikeMessage(props.message)}
                    />

                    <Show when={context.ticketGU()}>
                        <Button
                            icon='fa-regular fa-envelope'
                            type='ghost'
                            aria-label='Send as email'
                            onClick={() => showEmailDraftModal(props.message)}
                        />
                    </Show>
                </div>
            </Show>
        </div>
    );
}

function AssistantMessage(props: { message: Message }) {
    return (
        <div>
            <Rationales message={props.message} />
            <Markdown content={props.message.text} />
        </div>
    )
}

function Rationales(props: { message: Message }) {
    const rationales = createMemo(() => {
        const rationaleTraces = props.message.traces?.filter(t => t.type === 'agent.rationale');
        const rationaleEvents = rationaleTraces?.map(t => parseTrace(t)) as AgentRationaleEvent[];
        return rationaleEvents.map(e => e.text);
        return []
    })

    const [expanded, setExpanded] = createSignal(true);
    const loading = createMemo(() => props.message.text === undefined || props.message.text.length === 0);

    const message = createMemo(() => {
        if (!props.message.text) return 'Thinking...';
        return 'Thought for a few seconds...';
    })

    createEffect(() => {
        if (!loading()) {
            setExpanded(false);
        } else {
            setExpanded(true);
        }
    })

    return (
        <div class={styles['rationales-container']} data-expanded={expanded()}>
            <div class={`${styles['rationale']}`} classList={{ [styles['shine']]: loading(), }} onClick={() => setExpanded(!expanded())}>
                <span style='padding-right: 6px'>{message()}</span>
                <i class='fa fa-chevron-down' style={{
                    cursor: 'pointer',
                    "font-size": '0.9em',
                    transition: 'transform 0.2s ease-in-out',
                    transform: expanded() ? 'rotate(0deg)' : 'rotate(-90deg)',
                }}  />
            </div>

            <div class={styles['rationale-timeline']}>
                <For each={rationales().slice(0, -1) ?? []}>{(rationale) => (
                    <div>
                        <span class={styles['rationale-timeline-item']}>{rationale}</span>
                        <div class={styles['rationale-timeline-bar']}></div>
                    </div>
                )}</For>
                <Show when={!loading() && rationales().length}>
                    <div>
                        <span class={`${styles['rationale-timeline-item']}`}>{rationales().at(-1)}</span>
                    </div>
                </Show>
            </div>
            <Show when={loading()}>
                <div class={styles['rationale-timeline']}>
                    <span class={`${styles['rationale-timeline-item']} ${styles['shine']}`}>{rationales().at(-1) || 'Submitting query to Synergy AI'}</span>
                </div>
            </Show>
        </div>
    )
}

function ChatInput() {
    const context = requireSuppportAssistAgentContext();
    let textarea: HTMLTextAreaElement | undefined;

    return (
        <div class={styles['chat-input-container']} onClick={() => textarea?.focus()}>
            <textarea
                value={context.message()}
                rows="1"
                ref={textarea}
                class={styles['chat-input']}
                placeholder='Ask anything...'
                onInput={(e) => {
                    context.setMessage(e.target.value);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (context.message().trim()) {
                            context.sendMessage(context.message());
                        }
                    }
                }}
            />
            <Button icon='fa-solid fa-paper-plane' type='ghost' aria-label='Send' onClick={() => context.sendMessage(context.message())} />
        </div>
    )
}
